import { Link, useParams, useSearchParams } from "react-router-dom"
import {
  Arrow,
  Img,
  PostCard,
  Prose,
  Reveal,
  Section,
  SectionHeader,
  TextLink,
  FinalCta,
} from "../lib/shared"
import { useCollection, fmtDate } from "../lib/content"
import NotFound from "./NotFound"
import { Seo, articleSchema, breadcrumbSchema } from "../lib/seo"

export default function InsightDetail() {
  const { slug } = useParams()
  const [params] = useSearchParams()
  const { items } = useCollection("posts")

  // Unpublished posts are reachable only via an explicit ?preview=1 link from
  // the studio, and that view is never indexed.
  const preview = params.get("preview") === "1"
  const found = items.find((p) => p.slug === slug)
  const post = found && (found.status === "Published" || preview) ? found : null
  if (!post) return <NotFound />

  const more = items
    .filter((p) => p.status === "Published" && p.slug !== post.slug)
    .slice(0, 2)

  return (
    <>
      <Seo
        title={post.seoTitle || post.title}
        description={post.seoDescription || post.excerpt}
        image={post.cover}
        type="article"
        noindex={post.status !== "Published"}
        canonicalPath={`/insights/${post.slug}`}
        article={{
          publishedTime: post.date,
          modifiedTime: post.date,
          author: post.author,
          section: post.category,
        }}
        jsonLd={[
          articleSchema(post),
          breadcrumbSchema([
            { name: "Insights", path: "/insights" },
            { name: post.title, path: `/insights/${post.slug}` },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="bg-ink text-white">
        <div className="mx-auto max-w-[820px] px-6 pb-14 pt-40 lg:px-10 lg:pt-48">
          <Reveal>
            <Link
              to="/insights"
              className="group inline-flex items-center gap-2 text-[13px] font-medium text-neutral-400 transition-colors hover:text-white"
            >
              <Arrow className="h-4 w-4 rotate-180 transition-transform duration-300 group-hover:-translate-x-1" />
              All insights
            </Link>
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-8 flex flex-wrap items-center gap-3 text-[11.5px] font-medium uppercase tracking-[0.16em] text-neutral-400">
              <span className="text-accent-bright">{post.category}</span>
              <span>·</span>
              <span>{post.readTime}</span>
              <span>·</span>
              <span>{fmtDate(post.date)}</span>
            </div>
          </Reveal>
          <Reveal delay={140}>
            <h1 className="mt-5 text-[clamp(2.2rem,4.6vw,3.6rem)] font-black leading-[1.05] tracking-[-0.035em]">
              {post.title}
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 text-[17px] leading-[1.6] text-neutral-300">
              {post.excerpt}
            </p>
          </Reveal>
          {/* <Reveal delay={260}>
            <div className="mt-8 flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-[13px] font-bold">
                {post.author
                  .split(" ")
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <div className="text-[13px]">
                <div className="font-semibold text-white">{post.author}</div>
                <div className="text-neutral-400">Sableboxx</div>
              </div>
            </div>
          </Reveal> */}
        </div>
      </section>

      {/* Cover */}
      <section className="bg-ink">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <Reveal>
            <Img
              src={post.cover}
              priority
              alt={post.title}
              className="aspect-[16/8] w-full rounded-2xl"
            />
          </Reveal>
        </div>
        <div className="h-16 bg-ink lg:h-24 mb-16" />
      </section>

      {/* Body */}
      <Section className="bg-paper pb-24">
        <div className="mx-auto max-w-[720px]">
          <Reveal>
            <Prose text={post.body || post.excerpt} />
          </Reveal>

          <div className="mt-16 border-t border-neutral-200 pt-8">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 text-[14px] font-semibold text-ink"
            >
              Have a project in mind? Let&rsquo;s talk
              <Arrow className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </Section>

      {/* More posts */}
      {more.length > 0 && (
        <Section tone="mist">
          <SectionHeader
            eyebrow="Keep reading"
            title="Read more insights from Sableboxx"
            // action={<TextLink to="/insights">All articles</TextLink>}
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {more.map((a, i) => (
              <Reveal key={a.id} delay={i * 90}>
                <PostCard post={a} />
              </Reveal>
            ))}
          </div>
        </Section>
      )}

      <FinalCta />
    </>
  )
}
